import React from 'react';
import { Controller } from 'react-hook-form';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { productCardDefaultViews, productCardTextAlignments } from 'shared/constants/customization';
import { navBarColors, buttonsLinksColors } from 'shared/constants/menu-colors';

import { ActiveSwitch } from 'src/components/active-switch';
import useHasMobileApp from 'src/utils/use-has-mobile-app';
import { MenuCustomizationLayout } from '../components/menu-customization-layout';
import { OptionPicker } from '../components/option-picker';
import { ColorPickerV2 } from '../components/color-picker-v2';

import { useApplyToAllLocations } from '../use-apply-to-all-locations';
import { ECOMM_APP_V2_REDESIGN_FLAG } from '../homepage/add-section-modal/app-display-type-select';
import { useProductCardsForm } from './use-product-cards-form';
import { ProductCardsFormProps } from './product-card.types';

import { Heading, HelperText, Input, Inputs, Label, SubText } from '../customize.styles';
import { ProductCardPreview } from './product-card-preview';

/** Mobile app product card color customization; only rendered/applied when this flag is enabled in LaunchDarkly. */
export const ECOMM_APP_ADVANCED_CUSTOMIZATION_FLAG = 'ecomm.app.advanced-customization';

export function ProductCardsForm(props: ProductCardsFormProps): JSX.Element {
  const sectionName = 'Product Cards';
  const flags = useFlags();
  const hasMobileApp = useHasMobileApp();
  const showMobileAppSection = hasMobileApp && (flags[ECOMM_APP_V2_REDESIGN_FLAG] ?? false);
  const showAdvancedCustomization = showMobileAppSection && (flags[ECOMM_APP_ADVANCED_CUSTOMIZATION_FLAG] ?? false);
  const {
    control,
    handlePublish,
    handleReset,
    isDirty,
    previewData,
    applyAllInput,
    afterApplyAll,
  } = useProductCardsForm(props);
  const { handleApplyToLocations, hasAccessToAllLocations, chainId, totalLocationCount } = useApplyToAllLocations({
    input: applyAllInput,
    afterApplyAll,
    sectionName,
  });

  return (
    <MenuCustomizationLayout
      headerText={sectionName}
      subHeaderText='Select how product cards will appear.'
      isDirty={isDirty}
      handlePublish={() => void handlePublish()}
      handleReset={handleReset}
      previewComponent={
        <ProductCardPreview
          data={previewData}
          showMobileAppPreview={showMobileAppSection}
          showAdvancedCustomization={showAdvancedCustomization}
        />
      }
      handleApplyToLocations={handleApplyToLocations}
      hasAccessToAllLocations={hasAccessToAllLocations}
      chainId={chainId}
      totalLocationCount={totalLocationCount}
    >
      <Heading>Default View</Heading>
      <SubText>
        Select which product card type will display by default on category pages, search results and more.
      </SubText>

      <Inputs>
        <Input>
          <Controller
            name='defaultViewLarge'
            control={control}
            render={({ onChange, value }) => (
              <OptionPicker
                label='Large Screens'
                options={productCardDefaultViews}
                selectedOption={value}
                onChange={onChange}
              />
            )}
          />
          <HelperText>Includes desktop computers, laptops and tablets.</HelperText>
        </Input>

        <Input>
          <Controller
            name='defaultViewSmall'
            control={control}
            render={({ onChange, value }) => (
              <OptionPicker
                label='Small Screens'
                options={productCardDefaultViews}
                selectedOption={value}
                onChange={onChange}
              />
            )}
          />
          <HelperText>Includes mobile devices and smaller tablets.</HelperText>
        </Input>
      </Inputs>

      <Heading>Display Product Weight</Heading>
      <SubText>Display the product weight next to price on card view.</SubText>

      <Inputs>
        <Input>
          <Controller
            name='showProductWeight'
            control={control}
            render={({ onChange, value }) => (
              <ActiveSwitch name='showProductWeight' active={value} onClick={onChange} hasLabel />
            )}
          />
        </Input>
      </Inputs>

      <Heading>Full Width Images</Heading>
      <SubText>Display product images edge-to-edge on card view.</SubText>

      <Inputs>
        <Input>
          <Controller
            name='fullWidthImages'
            control={control}
            render={({ onChange, value }) => (
              <ActiveSwitch name='fullWidthImages' active={value} onClick={onChange} hasLabel />
            )}
          />
        </Input>
      </Inputs>

      <Heading>Image Cycling</Heading>
      <SubText>When enabled, product cards display only the primary image — no hover or touch image cycling.</SubText>

      <Inputs>
        <Input>
          <Controller
            name='disableProductCardImageCycling'
            control={control}
            render={({ onChange, value }) => (
              <ActiveSwitch name='disableProductCardImageCycling' active={value} onClick={onChange} hasLabel />
            )}
          />
        </Input>
      </Inputs>

      <Heading>Text Alignment</Heading>
      <SubText>Select text alignment for card view</SubText>

      <Inputs>
        <Input>
          <Controller
            name='textAlignment'
            control={control}
            render={({ onChange, value }) => (
              <OptionPicker
                label='Text Alignment'
                options={productCardTextAlignments}
                selectedOption={value}
                onChange={onChange}
              />
            )}
          />
        </Input>
      </Inputs>

      {showMobileAppSection && (
        <>
          <Heading>Mobile App</Heading>
          <SubText>Select which details display on mobile app product cards.</SubText>

          <Input>
            <Label>Show Staff Pick</Label>
            <Controller
              name='mobileShowStaffPick'
              control={control}
              render={({ onChange, value }) => (
                <ActiveSwitch name='mobileShowStaffPick' active={value} onClick={onChange} hasLabel />
              )}
            />
          </Input>

          <Input>
            <Label>Show Strain Type</Label>
            <Controller
              name='mobileShowStrainType'
              control={control}
              render={({ onChange, value }) => (
                <ActiveSwitch name='mobileShowStrainType' active={value} onClick={onChange} hasLabel />
              )}
            />
          </Input>

          <Input>
            <Label>Show Potency Details</Label>
            <Controller
              name='mobileShowPotencyDetails'
              control={control}
              render={({ onChange, value }) => (
                <ActiveSwitch name='mobileShowPotencyDetails' active={value} onClick={onChange} hasLabel />
              )}
            />
          </Input>

          <Input>
            <Label>Show Effects</Label>
            <Controller
              name='mobileShowEffects'
              control={control}
              render={({ onChange, value }) => (
                <ActiveSwitch name='mobileShowEffects' active={value} onClick={onChange} hasLabel />
              )}
            />
          </Input>

          <Input>
            <Label>Show Discount Names</Label>
            <Controller
              name='mobileShowDiscountNames'
              control={control}
              render={({ onChange, value }) => (
                <ActiveSwitch name='mobileShowDiscountNames' active={value} onClick={onChange} hasLabel />
              )}
            />
          </Input>

          <Inputs>
            <Input>
              <Label>Show Collection</Label>
              <Controller
                name='mobileShowCollection'
                control={control}
                render={({ onChange, value }) => (
                  <ActiveSwitch name='mobileShowCollection' active={value} onClick={onChange} hasLabel />
                )}
              />
            </Input>
          </Inputs>

          {showAdvancedCustomization && (
            <>
              <Heading>Mobile App Colors</Heading>
              <SubText>Customize the colors used on mobile app product cards.</SubText>

              <Inputs>
                <Input>
                  <Label>Card Background</Label>
                  <Controller
                    name='mobileBackgroundColor'
                    control={control}
                    render={({ onChange, value }) => (
                      <ColorPickerV2
                        label='Card Background'
                        colors={navBarColors}
                        onSelect={onChange}
                        selectedColor={value ?? '#fff'}
                      />
                    )}
                  />
                </Input>

                <Input>
                  <Label>Product Name</Label>
                  <Controller
                    name='mobileProductNameColor'
                    control={control}
                    render={({ onChange, value }) => (
                      <ColorPickerV2
                        label='Product Name'
                        colors={buttonsLinksColors}
                        onSelect={onChange}
                        selectedColor={value ?? '#121516'}
                      />
                    )}
                  />
                </Input>

                <Input>
                  <Label>Brand</Label>
                  <Controller
                    name='mobileBrandColor'
                    control={control}
                    render={({ onChange, value }) => (
                      <ColorPickerV2
                        label='Brand'
                        colors={buttonsLinksColors}
                        onSelect={onChange}
                        selectedColor={value ?? '#646d72'}
                      />
                    )}
                  />
                </Input>

                <Input>
                  <Label>Strain Type</Label>
                  <Controller
                    name='mobileStrainTypeColor'
                    control={control}
                    render={({ onChange, value }) => (
                      <ColorPickerV2
                        label='Strain Type'
                        colors={buttonsLinksColors}
                        onSelect={onChange}
                        selectedColor={value ?? '#2c3236'}
                      />
                    )}
                  />
                </Input>

                <Input>
                  <Label>Potency Values</Label>
                  <Controller
                    name='mobilePotencyColor'
                    control={control}
                    render={({ onChange, value }) => (
                      <ColorPickerV2
                        label='Potency Values'
                        colors={buttonsLinksColors}
                        onSelect={onChange}
                        selectedColor={value ?? '#646d72'}
                      />
                    )}
                  />
                </Input>

                <Input>
                  <Label>Price</Label>
                  <Controller
                    name='mobilePriceColor'
                    control={control}
                    render={({ onChange, value }) => (
                      <ColorPickerV2
                        label='Price'
                        colors={buttonsLinksColors}
                        onSelect={onChange}
                        selectedColor={value ?? '#2c3236'}
                      />
                    )}
                  />
                </Input>

                <Input>
                  <Label>Strike-Out Price</Label>
                  <Controller
                    name='mobileStrikethroughPriceColor'
                    control={control}
                    render={({ onChange, value }) => (
                      <ColorPickerV2
                        label='Strike-Out Price'
                        colors={buttonsLinksColors}
                        onSelect={onChange}
                        selectedColor={value ?? '#485055'}
                      />
                    )}
                  />
                </Input>
              </Inputs>
            </>
          )}
        </>
      )}
    </MenuCustomizationLayout>
  );
}
